import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/contexts/auth-context'
import { supabase } from '@/utils/supabase/client'
import { InventoryCategory } from '@/types'

interface CategoryFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  category?: InventoryCategory | null
}

export function CategoryFormModal({ isOpen, onClose, onSave, category }: CategoryFormModalProps) {
  const [formData, setFormData] = useState<Partial<InventoryCategory>>({})
  const [saving, setSaving] = useState(false)
  const { currentOrganization } = useAuth()

  useEffect(() => {
    setFormData(category || {})
  }, [category])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrganization) return
    setSaving(true)

    const { id, ...data } = formData

    const dataToSave = {
      ...data,
      organization_id: currentOrganization.id,
    }

    let result
    if (id) {
      result = await supabase.from('inventory_categories').update(dataToSave).eq('id', id)
    } else {
      result = await supabase.from('inventory_categories').insert(dataToSave)
    }

    const { error } = result

    if (!error) {
      onSave()
      onClose()
    } else {
      console.error(error)
    }
    setSaving(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit Category' : 'Add New Category'}</DialogTitle>
          <DialogDescription>
            Fill in the details for the category.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">Name</Label>
            <Input id="name" name="name" value={formData.name || ''} onChange={(e) => handleChange(e)} className="col-span-3" required />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">Description</Label>
            <Textarea id="description" name="description" value={formData.description || ''} onChange={(e) => handleChange(e)} className="col-span-3" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Category'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
} 